<template>
  <section>
    <div v-if="completionRequirements.length" class="completion-requirements" role="status">
      <h3>Current design needs attention</h3>
      <p>These findings reflect the current equipment and sizing validations. Review the affected sections below.</p>
      <ul>
        <li v-for="item in completionRequirements" :key="item.code">
          <strong>{{ item.equipmentType }}:</strong> {{ item.message }} {{ item.action }}
        </li>
      </ul>
    </div>
    <details v-if="originalCompletionRequirements.length" class="ai-draft-history">
      <summary>Original generation findings ({{ originalCompletionRequirements.length }})</summary>
      <p>Historical findings from the generated draft before equipment was removed or edited. These are not a current validation result. Recalculate to verify the current assembly.</p>
      <ul><li v-for="(item, index) in originalCompletionRequirements" :key="index">{{ item.message }}</li></ul>
    </details>
    <ul v-if="enablePopulateDesign && designReadinessIssues.length" class="completion-requirements">
      <li v-for="item in designReadinessIssues" :key="item.field">{{ item.message }}</li>
    </ul>
    <AISuggestionModal
      v-if="enablePopulateDesign"
      v-model:visible="showAIModal"
      :target-liquid-rate-stbd="targetLiquidRateStbd"
      :create-sizing="createSizing"
      :apply-recommendation="applyRecommendation"
      :unavailable-reason="recommendationUnavailableReason"
    />
    <fieldset
      :disabled="disabled || isScenarioReadonly || isImportedFromLegacy"
    >
      <OperatingParameters />
      <AISuggestionBanner
        v-if="enablePopulateDesign"
        :disabled="designReadinessIssues.length > 0 && !aiJobStatus"
        :message="designReadinessIssues.length ? 'Complete Well Data and Operating Parameters before generating an AI design.' : undefined"
        :job-status="aiJobStatus"
        @populate-design="showAIModal = true"
      />
      <!-- Dual Stack View when Lower Stack is enabled -->
      <div v-if="useLowerStack" class="dual-stack-section">
        <SplitEquipmentView />
      </div>
      <!-- Regular Equipment View when Lower Stack is not enabled -->
      <template v-else>
        <Pumps v-if="!isEspOnShore" v-model:disabled="disabled" />
        <Intakes />
        <Pumps v-if="isEspOnShore" v-model:disabled="disabled" />
        <SealSensorMle v-if="!isEspOnShore" />
        <Motor />
        <SealSensorMle v-if="isEspOnShore" />
        <Cables v-if="isEspOnShore" />
        <SurfaceDrive />
      </template>
    </fieldset>
  </section>
</template>

<script setup lang="ts">
import OperatingParameters from "@/views/scenarios/sizingParameters/OperatingParameters.vue";
import SealSensorMle from "@/views/scenarios/sizingParameters/SealSensorMle.vue";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import Intakes from "@/views/scenarios/sizingParameters/Intakes.vue";
import Cables from "@/views/scenarios/sizingParameters/Cables.vue";
import Motor from "@/views/scenarios/sizingParameters/Motor.vue";
import Pumps from "@/views/scenarios/sizingParameters/Pumps.vue";
import SurfaceDrive from "./sizingParameters/SurfaceDrive.vue";
import SplitEquipmentView from "@/components/scenarios/SplitEquipmentView.vue";
import AISuggestionBanner from "@/components/AISuggestionBanner.vue";
import AISuggestionModal from "@/components/AISuggestionModal.vue";
import { computed, ref, inject } from "vue";
import {
  sizingFormKey,
  equipmentFormKey,
  parametersFormKey,
  scenarioFormKey,
  scenarioKey,
  pumpsFormKey,
  housingsFormKey,
  intakesFormKey,
  sealsFormKey,
  cablesFormKey,
  scenarioValidatorKey,
  wellPropertiesFormKey,
  isDirtyKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { useGenericNotification } from "@/composables/logger/genericNotification";
import { useAISuggestionsStore } from "@/store/aiSuggestions";
import { useCreateRecommendedSizing } from "@/composables/useCreateRecommendedSizing";
import { applySizingRecommendation } from "@/libs/ai/applySizingRecommendation";
import { recommendationProvenance, equipmentSelectionSignature } from "@/libs/ai/recommendationProvenance";
import { useRouter } from "vue-router";
import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";
import { authenticatedJsonHeaders } from "@/libs/api/authenticatedHeaders";
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import { designInputIssues } from "@/libs/ai/designReadiness";
import { currentCompletionRequirements } from "@/libs/ai/currentCompletionRequirements";
import { Severity } from "@/composables/validation/scenarioValidator";

const disabled = ref(false);
const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();
const sizingForm = injectStrict(sizingFormKey, null);
const equipmentForm = injectStrict(equipmentFormKey);
const parametersForm = injectStrict(parametersFormKey, null);
const scenarioForm = injectStrict(scenarioFormKey);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const designReadinessIssues = computed(() => designInputIssues({
  wellProperties: wellPropertiesForm.data.value,
  parameters: parametersForm?.data.value
}));
const validator = injectStrict(scenarioValidatorKey);
const originalCompletionRequirements = computed(() => scenarioForm.data.value.ai_recommendation?.completionRequirements || []);
const completionRequirements = computed(() => currentCompletionRequirements(
  originalCompletionRequirements.value,
  equipmentForm.data.value,
  validator.errors.value.filter(error => error.severity >= Severity.error)
));
const isDirty = inject(isDirtyKey, () => false);
const sourceScenario = injectStrict(scenarioKey);
const recommendationForms = {
  parameters: parametersForm || undefined,
  equipment: equipmentForm,
  pumps: injectStrict(pumpsFormKey),
  housings: injectStrict(housingsFormKey),
  intakes: injectStrict(intakesFormKey),
  seals: injectStrict(sealsFormKey),
  cables: injectStrict(cablesFormKey)
};
const createRecommendedSizing = useCreateRecommendedSizing();
const router = useRouter();
const { fireToast } = useGenericNotification();
const createdSizing = ref<{ sizingId: string; scenarioId: string } | null>(null);
const equipment = computed(() => equipmentForm.data.value);
const showAIModal = ref(false);
const aiStore = useAISuggestionsStore();
const aiJobStatus = computed(() => {
  const id = scenarioForm.data.value.id;
  if (aiStore.equipmentContext?.scenarioId === id) {
    if (aiStore.loading) return aiStore.progress?.stage || "Queued or processing";
    if (aiStore.error && aiStore.durableJobs[id]) return "Monitoring interrupted — reopen to reconnect";
    if (Object.values(aiStore.equipmentOptions || {}).some(Boolean)) return "Results ready for review";
  }
  return aiStore.durableJobs[id] ? "Job saved — reopen to check progress or results" : "";
});
const enablePopulateDesign =
  window.config?.ENABLE_AI_DOS_POPULATE_DESIGN === "true";

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const useLowerStack = computed(() => {
  const hasLowerEquipStack = equipment.value.has_lower_equip_stack ?? false;
  const useUpperYTool = equipment.value.use_uphole_y_tool ?? false;
  const upperYToolSelected = !!equipment.value.r_uphole_y_tool;
  const useLowerYTool = equipment.value.use_downhole_y_tool ?? false;
  const lowerYToolSelected = !!equipment.value.r_downhole_y_tool;

  return (
    hasLowerEquipStack &&
    useUpperYTool &&
    upperYToolSelected &&
    useLowerYTool &&
    lowerYToolSelected
  );
});

const targetLiquidRateStbd = computed(() => {
  return parametersForm?.data.value.target_liquid_rate_std_barrel_day || 0;
});

const recommendationUnavailableReason = computed(() => {
  if (designReadinessIssues.value.length) return designReadinessIssues.value[0].message;
  if (
    isScenarioReadonly.value ||
    isImportedFromLegacy.value ||
    disabled.value
  ) {
    return "This scenario is read-only.";
  }
  if (
    !isEspOnShore.value ||
    equipment.value.has_lower_equip_stack ||
    equipment.value.use_uphole_y_tool ||
    equipment.value.use_downhole_y_tool ||
    equipment.value.include_y_tool
  ) {
    return "AI sizing population currently supports single-stack ESP on-shore scenarios without a Y-tool.";
  }
  if (!scenarioForm.data.value.winning_revision_id || isDirty()) {
    return "Save your sizing changes before generating, applying, or creating a recommended sizing. Recommendations use the saved scenario.";
  }
  return "";
});

async function validateRecommendationContext() {
  if (recommendationUnavailableReason.value)
    throw new Error(recommendationUnavailableReason.value);
  const context = useAISuggestionsStore().equipmentContext;
  if (
    context?.scenarioId !== scenarioForm.data.value.id ||
    context?.sizingId !== sizingForm?.data.value.id
  ) {
    throw new Error(
      "This recommendation belongs to a different scenario. Generate it again."
    );
  }
  if (!context?.sourceFingerprint) throw new Error("Generate a new recommendation to verify its source inputs.");
  await haltUntilCompletion();
  const response = await fetch(`/api/v1/sizing-report-context/${scenarioForm.data.value.id}`, {
    headers: await authenticatedJsonHeaders()
  });
  if (!response.ok) throw new Error("Cannot verify the latest sizing inputs. Reconnect and retry.");
  const latest = await response.json();
  if (latest.source?.inputFingerprint !== context.sourceFingerprint) {
    throw new Error("The source sizing has changed. Reload it and generate a new recommendation.");
  }
  if (isDirty()) throw new Error("Sizing inputs changed during verification. Save and generate a new recommendation.");
  // A pristine editor may still be stale relative to another user's save.
  if (latest.scenario?.winning_revision_id !== scenarioForm.data.value.winning_revision_id ||
      latest.parameters?.winning_revision_id !== parametersForm?.data.value.winning_revision_id ||
      latest.equipment?.winning_revision_id !== equipmentForm.data.value.winning_revision_id) {
    throw new Error("Reload the sizing to use the latest saved inputs before applying this recommendation.");
  }
}

async function applyRecommendation(option: AIEquipmentSuggestionOption) {
  await validateRecommendationContext();
  applySizingRecommendation(option, recommendationForms);
  scenarioForm.update({ ai_recommendation: {
    ...recommendationProvenance(option),
    appliedEquipmentSignature: equipmentSelectionSignature(equipmentForm.data.value)
  } });
  fireToast({
    severity: "success",
    summary: "Recommendation Applied",
    detail: "Equipment updated in the current sizing. Review the design, then Save to keep your changes.",
    life: 5000
  });
}

async function createSizing(option: AIEquipmentSuggestionOption) {
  await validateRecommendationContext();
  if (!sizingForm || !sourceScenario.value) throw new Error("Reload the source sizing before creating a design.");
  // Reuse the saved destination if navigation needs retrying after creation.
  if (!createdSizing.value) {
    createdSizing.value = await createRecommendedSizing(
      sizingForm.data.value, sourceScenario.value, option
    );
  }
  showAIModal.value = false;
  try {
    const failure = await router.push({ name: "SizingParameters", params: createdSizing.value });
    if (failure) throw new Error("The sizing was created, but could not be opened. Try again to open it.");
  } catch (error) {
    showAIModal.value = true;
    throw error;
  }
  fireToast({
    severity: "success",
    summary: "Sizing Created",
    detail: "The recommended sizing is ready for engineering review.",
    life: 5000
  });
}
</script>

<style lang="scss" scoped>
.completion-requirements {
  margin: 1rem 3rem;
  padding: 1rem;
  border: 1px solid var(--yellow-500);
  border-radius: 6px;
  background: var(--surface-card);
}
.anchor {
  position: relative;
  top: -15vh;
}

.dual-stack-section {
  margin-top: 2rem;
  padding: 1rem 0;
}
</style>
